import React, { useState, useEffect } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

// Utility function to remove country names from address strings
const removeCountryFromAddress = (addressString) => {
  if (!addressString) return "";
  
  // List of country names/variations to remove
  const countryPatterns = [
    /,\s*United States$/i,
    /,\s*USA$/i,
    /,\s*U\.S\.A\.$/i,
    /,\s*U\.S\.$/i,
    /,\s*US$/i,
    /,\s*Canada$/i,
  ];
  
  let cleaned = addressString.trim();
  
  // Remove country patterns from the end
  countryPatterns.forEach(pattern => {
    cleaned = cleaned.replace(pattern, "");
  });
  
  // Clean up any trailing commas or spaces
  cleaned = cleaned.replace(/,\s*$/, "").trim();
  
  return cleaned;
};

const PlaceComponent = ({ value, onChange, field, label, error }) => {
  const [address, setAddress] = useState(removeCountryFromAddress(value || ""));

  // Sync local state with value prop when it changes, and clean country names for display
  useEffect(() => {
    const cleanedValue = removeCountryFromAddress(value || "");
    setAddress(cleanedValue);
  }, [value]);

  const handleInputChange = (inputValue) => {
    setAddress(inputValue);
    // Clean country names before saving to parent (but allow free typing for autocomplete)
    const cleanedInput = removeCountryFromAddress(inputValue);
    onChange(field, cleanedInput);
  };

  const handleSelect = async (selected) => {
    try {
      const results = await geocodeByAddress(selected);
      if (results && results[0]) {
        const components = results[0].address_components;

        const getComponent = (type) => {
          const comp = components.find((c) => c.types.includes(type));
          return comp ? comp.long_name : "";
        };

        const streetNumber = getComponent("street_number");
        const streetName = getComponent("route");
        const city = getComponent("locality");
        const state = getComponent("administrative_area_level_1");
        const zip = getComponent("postal_code");

        // Build address parts array, only including non-empty components (NO COUNTRY)
        const addressParts = [];
        
        // Street address (street number + street name)
        const streetAddress = [streetNumber, streetName]
          .filter(part => part && part.trim())
          .join(" ")
          .trim();
        if (streetAddress) {
          addressParts.push(streetAddress);
        }
        
        // City
        if (city && city.trim()) {
          addressParts.push(city.trim());
        }
        
        // State and ZIP
        const stateZip = [state, zip]
          .filter(part => part && part.trim())
          .join(" ")
          .trim();
        if (stateZip) {
          addressParts.push(stateZip);
        }

        // Join all parts with commas (NO COUNTRY)
        const formatted = addressParts.join(", ");

        setAddress(formatted);
        onChange(field, formatted);
      }
    } catch (err) {
      console.error("Error fetching place details:", err);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const input = document.querySelector(".pac-target-input");
      if (input) {
        input.setAttribute("placeholder", "");

        const observer = new MutationObserver(() => {
          input.setAttribute("placeholder", "");
        });
        observer.observe(input, {
          attributes: true,
          attributeFilter: ["placeholder"],
        });

        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="place-wrapper">
      <label className="form-label">
        {label}
        <span className="text-danger">*</span>
      </label>
      <PlacesAutocomplete
        value={address}
        onChange={handleInputChange}
        onSelect={handleSelect}
        debounce={300}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="autocomplete-root">
            <input
              {...getInputProps({
                placeholder: "",
                className: "form-control",
              })}
            />

            {(loading || suggestions.length > 0) && (
              <div className="autocomplete-dropdown">
                {loading && <div className="suggestion-item">Loading...</div>}

                {suggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? "suggestion-item active"
                    : "suggestion-item";
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, { className })}
                      key={suggestion.placeId}
                    >
                      {suggestion.description}
                    </div>
                  );
                })}
              </div>
            )}
            {error && <div className="text-danger mt-1">{error}</div>}
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );
};

export default PlaceComponent;
